import _ from 'lodash'
import moment from 'moment/moment'
import { call, select } from 'redux-saga/effects'

import biMessages from 'core/data-model/constants/bi-messages'
import DEFAULT from 'core/data-model/constants/defaults'
import values from 'core/data-model/constants/values'
import { christianToThaiWithoutFormat } from 'core/service/lib/date'
import { round } from 'core/service/lib/number-format'
import { mainFields } from 'core/service-wrapper/partner-api/handler-request/service-field-main'
import { partnerApiUtils } from 'core/service-wrapper/partner-api/handler-request/service-utils'
import { getIdentityUser, isLoggedIn } from 'identity/selectors'
import { getQuickQuoteInsured } from 'quick-quote/insured-information/selectors'
import { getCalculatedCoveragePeriod } from 'quick-quote/product-common/benefit-illustration/selectors'
import * as commonCoverageSelectors from 'quick-quote/product-common/coverage-plan/selectors'
import { getSelectedPlan, getSelectedRiders } from 'quick-quote/product-common/coverage-plan/selectors'
import * as investmentCoverageSelectors from 'quick-quote/product-investment/coverage-plan/selectors'
import * as lstuFundSelector from 'quick-quote/product-investment/fund-allocation/lstu/selectors'
import * as rppFundSelector from 'quick-quote/product-investment/fund-allocation/rpp/selectors'
import * as investmentRiskSelector from 'quick-quote/product-investment/risk-questionnaire/selectors'
import * as loanCoverageSelectors from 'quick-quote/product-loan/coverage-plan/redux'
import * as mrtaCoverageSelectors from 'quick-quote/product-mrta/coverage-plan/selectors'
import { getPremiumPerMonthGLTSP } from 'quick-quote/product-mrta/coverage-plan/selectors'
import { getSelectedDisplayProduct } from 'quick-quote/product-selection/selectors'
import { getRiderBenefitTables } from 'quick-quote/product-whole-life/benefit-illustration/selectors'
import { getVersion } from 'quick-quote/selectors'
import { store as qqStore } from 'store'

const { birthdate, modalFactor, defaultIfEmpty, defaultBirthdate } = partnerApiUtils

function defaultInsuredBirthdate(insuredBirthdate) {
  return birthdate.toApi(defaultIfEmpty(insuredBirthdate, defaultBirthdate(DEFAULT.DEFAULT_INSURED_AGE)))
}

function defaultPayerBirthdate(payerBirthdate) {
  return birthdate.toApi(defaultIfEmpty(payerBirthdate, defaultBirthdate(DEFAULT.DEFAULT_PAYER_AGE)))
}

export const quickQuotePolicy = {
  /**
   * insured
   */
  insured: (state) => {
    const insured = getQuickQuoteInsured(state)

    return {
      title: insured.title.name.text,
      firstName: defaultIfEmpty(insured.firstName.name, 'ลูกค้า'),
      lastName: defaultIfEmpty(insured.lastName.name, 'คนพิเศษ'),
      sex: defaultIfEmpty(insured.gender, DEFAULT.DEFAULT_INSURED_GENDER),
      birthDate: defaultInsuredBirthdate(insured.birthdate.value),
      mainOccupationCode: insured.occupation.code,
      mainNatureOfDuty: defaultIfEmpty(insured.occupation.natureOfDuty.code, 'ND0444'),
    }
  },
  /**
   * payer
   */
  payer: (state) => {
    const { payerNeeded, payer } = state.insured
    if (!payerNeeded) {
      return undefined
    }

    return {
      title: '',
      firstName: '',
      lastName: '',
      sex: defaultIfEmpty(payer.gender, DEFAULT.DEFAULT_PAYER_GENDER),
      birthDate: defaultPayerBirthdate(payer.birthdate.value),
      mobileNumber: '',
      email: '',
      nationality: '',
      nationalityCode: '',
      weight: '',
      height: '',
      maritalStatus: '',
      maritalStatusCode: '',
      documentShow: '',
      mainOccupationCode: payer.occupation.code,
      mainNatureOfDuty: defaultIfEmpty(payer.occupation.natureOfDuty.code, 'ND0444'),
      mainIncomeAmt: '',
      mainWorkAt: '',
      addresses: [],
      relationship: payer.relation,
      appFormAnswers: [],
    }
  },
  /**
   * seller
   */
  seller: async (state) => {
    if (!isLoggedIn(state)) {
      return {
        distributionChannel: 'AZ',
        distributorGroup: 'AGENT',
        userAgentType: 'AGENT',
      }
    }

    // seller
    let user = getIdentityUser(state)
    const [sellerInformation] = await mainFields.seller(user)

    return sellerInformation
  },
  /**
   * basicPlan
   */
  basicPlan: (state) => {
    const selectedPlan = getSelectedPlan(state)
    const { category, basicPlanCode, productRefName } = getSelectedDisplayProduct(state)
    const basicPlan = {
      category,
      basicPlanCode: basicPlanCode,
      productRefName: productRefName,
      classCode: 'S',
    }
    if (!_.isEmpty(selectedPlan)) {
      basicPlan.basicPlanCode = selectedPlan
    }

    const autoRound = (value) => {
      if (typeof value === 'number') return round(value, 0)
      return value
    }

    if (category === biMessages.CATEGORY_INVESTMENT) {
      basicPlan.modalFactor = modalFactor.toSymbol(investmentCoverageSelectors.getSelectedModelFactorID(state))
      basicPlan.RPP = autoRound(investmentCoverageSelectors.getRegularPremium(state).value)
      basicPlan.RTU = autoRound(investmentCoverageSelectors.getRegularTopUp(state).value)
      basicPlan.LSTU = investmentCoverageSelectors.getLumpSumTopUp(state)
      basicPlan.basicSA = investmentCoverageSelectors.getSumAssured(state).value
    } else if (category === biMessages.CATEGORY_LOAN) {
      basicPlan.modalFactor = modalFactor.toSymbol(commonCoverageSelectors.getSelectedModelFactorID(state))
      basicPlan.basicPremium = loanCoverageSelectors.getPremium(state)
      basicPlan.basicSA = loanCoverageSelectors.getSumAssured(state)
      basicPlan.loanAmount = loanCoverageSelectors.getLoanAmount(state)
      basicPlan.coverageTerm = loanCoverageSelectors.getCoverageTerm(state)
      basicPlan.loanPaymentMethod = loanCoverageSelectors.getPaymentMethod(state)
      basicPlan.interestRate = mrtaCoverageSelectors.getInterestRate(state).value
    } else if (category === biMessages.CATEGORY_MRTA) {
      basicPlan.modalFactor = modalFactor.toSymbol(commonCoverageSelectors.getSelectedModelFactorID(state))
      basicPlan.basicPremium = commonCoverageSelectors.getBasicPremium(state)
      basicPlan.basicSA = commonCoverageSelectors.getSumAssured(state)
      basicPlan.loanAmount = mrtaCoverageSelectors.getLoanAmount(state)
      basicPlan.coverageTerm = mrtaCoverageSelectors.getCoveragePeriod(state)
      basicPlan.loanPaymentMethod = loanCoverageSelectors.getPaymentMethod(state)
      basicPlan.interestRate = mrtaCoverageSelectors.getInterestRate(state).value
      basicPlan.loanPeriod = mrtaCoverageSelectors.getLoanPeriod(state)
      basicPlan.sumAssuredContract = mrtaCoverageSelectors.getSumAssuredContract(state)
    } else {
      basicPlan.modalFactor = modalFactor.toSymbol(commonCoverageSelectors.getSelectedModelFactorID(state))
      basicPlan.basicPremium = commonCoverageSelectors.getBasicPremium(state)
      basicPlan.basicSA = commonCoverageSelectors.getSumAssured(state)
    }

    if (category === biMessages.CATEGORY_BUMNAN_READY || category === biMessages.CATEGORY_RETIREMENT) {
      const pensionType = commonCoverageSelectors.getPensionType(state)
      // prettier-ignore
      basicPlan.pensionType = pensionType === values.PENSION_MONTHLY ? values.PENSION_MONTHLY : values.PENSION_ANNUALLY
      basicPlan.annuityTypeCode = quickQuotePolicy.pensionType(basicPlan.pensionType)
    }

    return basicPlan
  },
  pensionType: (pensionType) => {
    return pensionType === values.PENSION_ANNUALLY ? 'AT0001' : pensionType === values.PENSION_MONTHLY ? 'AT0002' : ''
  },
  pensionTypeName: (pensionType) => {
    // prettier-ignore
    return pensionType === 'AT0001' || pensionType === values.PENSION_ANNUALLY
      ? 'รายปี'
      : pensionType === 'AT0002' || pensionType === values.PENSION_MONTHLY
        ? 'รายเดือน'
        : undefined
  },
  riders: (state) => {
    const selectedRiders = commonCoverageSelectors.getSelectedRiders(state)
    return selectedRiders.map((rider, i) => ({
      productRiderCode: rider.selectedPlan.fullPlanCode?.length
        ? rider.selectedPlan.fullPlanCode
        : rider.selectedPlan.planCode?.length
        ? rider.selectedPlan.planCode
        : rider.code,
      coverageValue: rider.selectedPlan.sumAssured ?? rider.sumAssured,
      orderSequence: i + 1,
      aura: rider.aura ?? {}, // fulfil for aura transformData
    }))
  },
  /**
   * funds
   * @returns {{[isCustomLstuFunds]: boolean, [funds]: {fundCode, fundAllocation}[], [lstuFunds]: {fundCode, fundAllocation}[]}}
   */
  funds: (state) => {
    let funds = []
    let lstuFunds = []
    let lstuSelectedAllocationOption

    if (state.selectedDisplayProduct.category === biMessages.CATEGORY_INVESTMENT) {
      const mappingFund = (selectedFunds) => {
        return selectedFunds.map((selectedFund) => ({
          fundCode: selectedFund.code,
          fundAllocation: selectedFund.fundAllocation,
        }))
      }

      // rpp
      const rppFundAllocations = rppFundSelector.getRppFundAllocations(state)
      const selectedRppFunds = rppFundAllocations.filter((fund) => fund.selected)
      funds = mappingFund(selectedRppFunds)

      // lstu
      lstuSelectedAllocationOption = lstuFundSelector.getSelectedAllocationOption(state)
      if (lstuSelectedAllocationOption === values.LSTU_FUND_ALLOCATION_OPTION.SAME_AS_RPP) {
        lstuFunds = funds
      } else if (lstuSelectedAllocationOption === values.LSTU_FUND_ALLOCATION_OPTION.INDEPENDENT) {
        const lstuFundAllocations = lstuFundSelector.getLstuFundAllocations(state)
        const selectedLstuFunds = lstuFundAllocations.filter((fund) => fund.selected)
        lstuFunds = mappingFund(selectedLstuFunds)
      }
    }

    return {
      isCustomLstuFunds: lstuSelectedAllocationOption === values.LSTU_FUND_ALLOCATION_OPTION.INDEPENDENT,
      funds,
      lstuFunds,
    }
  },
  /**
   * rpqAnswers
   */
  rpqAnswers: (state) => {
    if (state.selectedDisplayProduct.category !== biMessages.CATEGORY_INVESTMENT) {
      return []
    }

    const questionnaires = investmentRiskSelector.getQuestionnaire(state)
    return questionnaires.map((questionnaire) => {
      const answerCode = questionnaire.multipleChoice
        ? undefined
        : questionnaire.choices.find((choice) => choice.value === questionnaire.selectedChoice).valueText

      // prettier-ignore
      return {
        questionCode: questionnaire.questionCode,
        orderSequence: questionnaire.id,
        answerMode: questionnaire.answerMode,
        answerCode: answerCode,
        answerChoice: answerCode,
        answerOptionals: questionnaire.multipleChoice
          ? questionnaire.choices
            .filter(choice => questionnaire.selectedChoice.includes(choice.value))
            .map(choice => ({ optionalCode: choice.valueText, orderSequence: choice.value }))
          : undefined,
      }
    })
  },
}

export const partnerApiProposal = {
  createPayloadFromQuickQuoteState: function*() {
    const state = qqStore.getState()
    const insured = quickQuotePolicy.insured(state)
    const payer = quickQuotePolicy.payer(state)
    const basicPlan = quickQuotePolicy.basicPlan(state)
    const riders = quickQuotePolicy.riders(state)
    const { isCustomLstuFunds, funds, lstuFunds } = quickQuotePolicy.funds(state)
    const rpqAnswers = quickQuotePolicy.rpqAnswers(state)

    const user = getIdentityUser(state)
    let [seller, profile] = yield call(mainFields.seller, user)
    const version = yield select(getVersion)

    const totalPremium = commonCoverageSelectors.getTotalPremium(state)
    let premiumPerMonth = undefined
    if (values.MRTA === basicPlan.category) {
      premiumPerMonth = yield select(getPremiumPerMonthGLTSP)
    }

    const fulfilRiders = yield select(getSelectedRiders)
    const fulfilRiderBenefitData = yield select(getRiderBenefitTables)
    const fulfilCalCoveragePeriod = yield select(getCalculatedCoveragePeriod)

    return {
      usePartnerApi: true,
      profile, // fulfil
      version: version.appVersion, // fulfil
      datetime: christianToThaiWithoutFormat().format('Do MMMM YYYY เวลา kk:mm'), // fulfil
      // birthdate: partnerApiUtils.birthdate.toFrontEnd(insured.birthDate), // fulfil for pdf sign
      // distributorType: seller.distributorGroup, // fulfil for pdf sign
      // productCategory: basicPlan.category,
      riders: fulfilRiders,
      riderBenefitData: fulfilRiderBenefitData,
      calculatedCoveragePeriod: fulfilCalCoveragePeriod,
      data: {
        category: basicPlan.category,
        planCode: basicPlan.basicPlanCode,
        planName: basicPlan.productRefName,
        effectiveDate: moment().format('YYYY-MM-DD'),
        emailReceivePolicy: '',
        totalPremium: totalPremium,
        paidAmount: 0,
        paymentPeriod: '',
        paymentPeriodCode: basicPlan.modalFactor,
        coverageValue: basicPlan.basicSA,
        basPremium: basicPlan.RPP ?? 0,
        topUpPremium: basicPlan.RTU ?? 0,
        initailLumpsumPremium: basicPlan.LSTU ?? 0,
        classCode: basicPlan.classCode,
        annuityTypeCode: basicPlan.annuityTypeCode,
        annuityTypeName: quickQuotePolicy.pensionTypeName(basicPlan.pensionType),
        loanAmount: basicPlan.loanAmount,
        loanPeriod: basicPlan.loanPeriod,
        coverageTerm: basicPlan.coverageTerm,
        loanPaymentMethod: basicPlan.loanPaymentMethod,
        interestRate: basicPlan.interestRate,
        sumAssuredContract: basicPlan.sumAssuredContract,
        premiumPerMonth: premiumPerMonth,
        productRiders: riders,
        insureds: [
          {
            title: insured.title,
            firstName: insured.firstName,
            lastName: insured.lastName,
            sex: insured.sex,
            birthDate: insured.birthDate,
            mobileNumber: '',
            email: '',
            nationality: '',
            nationalityCode: '',
            weight: 0.0,
            height: 0.0,
            maritalStatusCode: '',
            documentShow: '',
            useMotorCycle: false,
            mainOccupationCode: insured.mainOccupationCode,
            mainNatureOfDuty: insured.mainNatureOfDuty,
            mainIncomeAmt: '',
            mainWorkAt: '',
            addresses: [],
            lstuCustomFundFlag: isCustomLstuFunds,
            fundsInfo: funds.map((item, index) => ({
              fundCode: item.fundCode,
              fundPercentage: item.fundAllocation,
              fundSequence: index,
            })),
            lstuFundsInfo: lstuFunds.map((item, index) => ({
              fundCode: item.fundCode,
              fundPercentage: item.fundAllocation,
              fundSequence: index,
            })),
            rpqAnswers: rpqAnswers.map((answer) => {
              if (answer.answerOptionals) {
                answer.answerOptionals = answer.answerOptionals.map((option) => option.optionalCode)
              }
              return answer
            }),
            pdpaAnswers: [],
            taxAnswers: [],
            fatcaAnswers: [],
          },
        ],
        ePolicyDeliveryFlag: '',
        crsPersonFlag: '',
        sellerInformation: [seller],
        payers: _.compact([payer]),
      },
    }
  },
}
